import './PendingPayments.component.scss';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { EBankKeys, IBankPendingPayment } from '../../../domain/bank.interface';
import { EFeeType, TFeeType } from '../../../domain/group.interface';
import { loadBankPendingPayments, resetBank } from '../../../store/bank/bank.actions';
import { makeSelectPendingPayments } from '../../../store/bank/bank.selectors';
import { formatNumberToCurrency } from '../../../utils/FormNormalizers';
import { NavbarComponent, NavigationMenuComponent, TpvModalFormComponent } from '../../organisms/organisms.index';

function PendingPaymentItem(props: any) {
  const { id, group, amount, feeType, expired, payFunc } = props;
  const { name: groupName, payLimitDate } = group;
  const date = moment(new Date(Date.parse(payLimitDate))).format('DD/MM/YYYY');

  return (
    <div className='minicard pending-payment'>
      <div className='minicard-info capitalize'>
        <h6>{groupName}</h6>
        {payLimitDate && (
          <p>
            <FormattedMessage id='common.limit-date' />: {date}
          </p>
        )}
      </div>
      {!!amount && (
        <div className='minicard-left-text'>
          <div className='amount-container'>
            <p className='variable-amount'>{formatNumberToCurrency(amount)}</p>
            {feeType !== EFeeType.FIXED && (
              <p className='suggested'>
                <FormattedMessage id='common.suggested'></FormattedMessage>
              </p>
            )}
          </div>
        </div>
      )}
      <div className='minicard-buttons'>
        {expired ? (
          <div className='expired'>
            <FontAwesomeIcon icon='times-circle' />
          </div>
        ) : (
          <button
            className='btn btn-lili'
            onClick={() => payFunc(id, amount, feeType)}
          >
            <FormattedMessage id='common.pay' />
          </button>
        )}
      </div>
    </div>
  );
}

export function PendingPaymentsComponent(props: any) {
  const dispatch = useDispatch();
  const pendingPaymentReducer: IBankPendingPayment[] = useSelector(
    makeSelectPendingPayments(),
  );
  const [pendingPayments, setPendingPayments] = useState(pendingPaymentReducer);

  const unexpiredPP = pendingPayments.filter(
    (el: IBankPendingPayment) =>
      (new Date(Date.parse(el.group.payLimitDate)) > new Date() ||
        !el.group.payLimitDate) &&
      !el.group.isUsedMoneybox,
  );
  const expiredPP = pendingPayments.filter(
    (el: IBankPendingPayment) =>
      (el.group.payLimitDate &&
        new Date(Date.parse(el.group.payLimitDate)) <= new Date()) ||
      el.group.isUsedMoneybox,
  );
  const ppTop = pendingPayments.filter((el: IBankPendingPayment) => !el.paid);
  const [showTpvModal, setShowTpvModal] = useState(false);
  const [pendingPaymentId, setPendingPaymentId] = useState('');
  const [propsAmount, setPropsAmount] = useState(0);
  const [amountType, setAmountType] = useState(EFeeType.FIXED);

  const handleTpvModalShow = (
    ppId: string,
    amount: number,
    feeType: TFeeType,
  ) => {
    setPendingPaymentId(ppId);
    setPropsAmount(amount);
    setAmountType(feeType);
    setShowTpvModal(true);
  };

  const handleTpvModalClose = () => {
    dispatch(loadBankPendingPayments());
    setPendingPaymentId('');
    setPropsAmount(0);
    setAmountType(EFeeType.FIXED);
    setShowTpvModal(false);
  };

  useEffect(() => {
    dispatch(loadBankPendingPayments());
    return () => {
      dispatch(resetBank(EBankKeys.pendingPayments));
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const pending = pendingPaymentReducer.filter(
      (pp: IBankPendingPayment) => !pp.paid,
    );
    setPendingPayments(pending);
  }, [pendingPaymentReducer]);

  return (
    <div className='page-container'>
      <NavbarComponent />
      <NavigationMenuComponent {...props} />
      <div className='page-title'>
        <h4>
          <FormattedMessage id='pages.my-bank.main.link_pendingPayments' />
        </h4>
      </div>
      <div className='content-wrapper'>
        {!!unexpiredPP.length && (
          <div className='mb-5'>
            <h6 className='subtitle'>
              <FormattedMessage id={`pages.pending-payments.unexpired-title`} />
            </h6>
            <ul>
              {unexpiredPP.map((el: IBankPendingPayment, idx: number) => (
                <PendingPaymentItem
                  key={`unexpired-${idx}`}
                  {...el}
                  expired={false}
                  payFunc={handleTpvModalShow}
                />
              ))}
            </ul>
          </div>
        )}
        {!!expiredPP.length && (
          <div>
            <h6 className='subtitle'>
              <FormattedMessage id={`pages.pending-payments.expired-title`} />
            </h6>
            <ul>
              {expiredPP.map((el: IBankPendingPayment, idx: number) => (
                <PendingPaymentItem
                  key={`expired-${idx}`}
                  {...el}
                  expired={true}
                />
              ))}
            </ul>
          </div>
        )}
        {!ppTop.length && (
          <p className='text-primary'>
            <FormattedMessage id='pages.my-bank.no_data' />
          </p>
        )}
        {showTpvModal && (
          <TpvModalFormComponent
            {...props}
            showModal={showTpvModal}
            handleClose={handleTpvModalClose}
            pendingPaymentId={pendingPaymentId}
            amount={propsAmount}
            variableAmount={
              amountType === EFeeType.VARIABLE || amountType === EFeeType.HIDDEN
            }
          />
        )}
      </div>
    </div>
  );
}
